/**
 * @module components/AmbientMode/scenes/NetworkScene
 * @description Escena de red de controles ISO para el modo ambient (40 segundos por rotación).
 *
 * Visualiza los controles ISO 27002 como nodos en un grafo SVG animado con:
 * - Nodos coloreados según el estado del control (completado, en progreso, no iniciado)
 * - Aristas animadas con pulso de datos entre controles relacionados
 * - Eventos flotantes que aparecen y desaparecen mostrando actividad reciente
 * - Etiquetas de categoría como centros del grafo
 *
 * Los nodos se posicionan en un layout circular por categoría ISO.
 * La animación de pulso en las aristas usa `pulseDelayMs` para crear efecto de cascada.
 *
 * @see NetworkNode y NetworkEdge en types/ambient.ts para los tipos de datos
 * @see SceneEngine que monta esta escena durante 40s antes de rotar
 */
import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useData } from '../../../contexts/DataContext';
import type { NetworkEdge, NetworkEvent, NetworkNode } from '../../../types/ambient';

// ─── Constants ────────────────────────────────────────────────────────────────

const VB_W  = 1000;
const VB_H  = 620;
const CX    = VB_W / 2;
const CY    = VB_H / 2;
const ORBIT_R = 230;

const EVENT_LABELS = [
  'Control actualizado',
  'Evidencia agregada',
  'Tarea completada',
  'Revisión programada',
  'Asignación realizada',
  'Estado revisado',
  'Control en progreso',
];

const STATUS_COLORS: Record<string, string> = {
  completed:    '#10b981',
  in_progress:  '#3b82f6',
  not_started:  '#4b5563',
  non_compliant:'#ef4444',
};

// ─── Graph builder ────────────────────────────────────────────────────────────

function buildGraph(
  categories: Array<{ label: string; total: number; completed: number; status: string }>
): { nodes: NetworkNode[]; edges: NetworkEdge[] } {
  const N = categories.length;
  if (N === 0) return { nodes: [], edges: [] };

  const nodes: NetworkNode[] = categories.map((cat, i) => {
    const angle = (2 * Math.PI * i) / N - Math.PI / 2;
    return {
      id: `cat-${i}`,
      label: cat.label,
      status: cat.status as NetworkNode['status'],
      cx: CX + ORBIT_R * Math.cos(angle),
      cy: CY + ORBIT_R * Math.sin(angle),
      radius: 30 + Math.round((cat.completed / Math.max(cat.total, 1)) * 12),
      completionRate: cat.total > 0 ? (cat.completed / cat.total) * 100 : 0,
    };
  });

  // Nodo central (hub)
  const hub: NetworkNode = {
    id: 'hub',
    label: 'SGSI',
    status: 'in_progress',
    cx: CX,
    cy: CY,
    radius: 42,
    completionRate: 0,
  };
  const allNodes = [hub, ...nodes];

  // Edges: hub → cada nodo satélite
  const edges: NetworkEdge[] = nodes.map((node, i) => ({
    id: `edge-hub-${i}`,
    fromId: 'hub',
    toId: node.id,
    pulseDelayMs: i * 600,
    x1: hub.cx,
    y1: hub.cy,
    x2: node.cx,
    y2: node.cy,
  }));

  // Edges: nodo → siguiente (anillo)
  nodes.forEach((node, i) => {
    const next = nodes[(i + 1) % nodes.length];
    edges.push({
      id: `edge-ring-${i}`,
      fromId: node.id,
      toId: next.id,
      pulseDelayMs: 300 + i * 400,
      x1: node.cx,
      y1: node.cy,
      x2: next.cx,
      y2: next.cy,
    });
  });

  return { nodes: allNodes, edges };
}

// ─── Pulse dot along an edge ──────────────────────────────────────────────────
// Usamos CSS @keyframes + transform para que animation-play-state funcione
// (SVG SMIL animateMotion no responde a animation-play-state).

interface PulseDotProps {
  edge: NetworkEdge;
  color: string;
}

function PulseDot({ edge, color }: PulseDotProps) {
  const { x1, y1, x2, y2, pulseDelayMs } = edge;
  const dx = x2 - x1;
  const dy = y2 - y1;
  const duration = 2_500 + Math.abs(pulseDelayMs % 1_000);

  return (
    <circle
      r={3}
      fill={color}
      opacity={0.85}
      cx={x1}
      cy={y1}
      style={{
        animation: `pulse-along-edge-${edge.id} ${duration}ms linear infinite`,
        animationDelay: `${pulseDelayMs}ms`,
      }}
    >
      {/* SMIL animateMotion como fallback visual únicamente — la pausa real
          la maneja la clase .ambient-paused en el contenedor */}
      <animateMotion
        dur={`${duration}ms`}
        repeatCount="indefinite"
        begin={`${pulseDelayMs}ms`}
        calcMode="linear"
      >
        <mpath href={`#${edge.id}`} />
      </animateMotion>
    </circle>
  );
}

// ─── Network event label (flotante) ──────────────────────────────────────────

function FloatingEvent({ event }: { event: NetworkEvent }) {
  return (
    <div
      className="absolute pointer-events-none text-sm font-mono text-blue-300/80 animate-[fadeInOut_3s_ease-in-out_forwards]"
      style={{
        left: `${event.x * 100}%`,
        top:  `${event.y * 100}%`,
        transform: 'translate(-50%, -50%)',
      }}
    >
      ◉ {event.label}
    </div>
  );
}

// ─── Main scene ──────────────────────────────────────────────────────────────

let _eventId = 0;

export const NetworkScene = memo(function NetworkScene() {
  const { filteredControls } = useData();
  const svgRef = useRef<SVGSVGElement>(null);
  const [events, setEvents] = useState<NetworkEvent[]>([]);

  // Agrupar controles por categoría
  const categories = useMemo(() => {
    const map = new Map<string, { total: number; completed: number; inProgress: number }>();
    filteredControls.forEach(c => {
      const key = c.category ?? 'General';
      const entry = map.get(key) ?? { total: 0, completed: 0, inProgress: 0 };
      entry.total++;
      if (c.status === 'completed')  entry.completed++;
      if (c.status === 'in_progress') entry.inProgress++;
      map.set(key, entry);
    });

    return Array.from(map.entries())
      .map(([label, d]) => ({
        label: label.length > 18 ? label.slice(0, 16) + '…' : label,
        ...d,
        status: d.completed === d.total && d.total > 0
          ? 'completed'
          : d.inProgress > 0
          ? 'in_progress'
          : d.total > 0
          ? 'not_started'
          : 'not_started',
      }))
      .slice(0, 8); // máximo 8 nodos satélite
  }, [filteredControls]);

  const { nodes, edges } = useMemo(() => buildGraph(categories), [categories]);

  // Eventos flotantes — se generan cada 4s y desaparecen a los 3s
  useEffect(() => {
    const interval = setInterval(() => {
      if (document.hidden) return;
      const id = `ev-${++_eventId}`;
      const label = EVENT_LABELS[Math.floor(Math.random() * EVENT_LABELS.length)];
      const newEvent: NetworkEvent = {
        id,
        label,
        x: 0.2 + Math.random() * 0.6,
        y: 0.15 + Math.random() * 0.7,
        createdAt: Date.now(),
      };
      setEvents(prev => [...prev.slice(-4), newEvent]);
      setTimeout(() => {
        setEvents(prev => prev.filter(e => e.id !== id));
      }, 3_000);
    }, 4_000);

    return () => clearInterval(interval);
  }, []);

  // SVG SMIL pause/resume vía DOM API cuando pestaña oculta
  useEffect(() => {
    const handleVisibility = () => {
      const svg = svgRef.current;
      if (!svg) return;
      if (document.hidden) {
        svg.pauseAnimations();
      } else {
        svg.unpauseAnimations();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  return (
    <div className="relative w-full h-full flex items-center justify-center overflow-hidden">
      {/* SVG graph */}
      <svg
        ref={svgRef}
        viewBox={`0 0 ${VB_W} ${VB_H}`}
        className="w-full h-full max-h-full"
        preserveAspectRatio="xMidYMid meet"
      >
        {/* Definir paths para los edges (usados por animateMotion) */}
        <defs>
          {edges.map(e => (
            <path
              key={e.id}
              id={e.id}
              d={`M ${e.x1} ${e.y1} L ${e.x2} ${e.y2}`}
            />
          ))}
          {/* Radial gradient para el hub */}
          <radialGradient id="hub-gradient" cx="50%" cy="50%" r="50%">
            <stop offset="0%"   stopColor="#3b82f6" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#1e3a5f" stopOpacity="0"   />
          </radialGradient>
        </defs>

        {/* Halo del hub */}
        <circle cx={CX} cy={CY} r={280} fill="url(#hub-gradient)" />

        {/* Edges */}
        {edges.map(e => (
          <line
            key={e.id}
            x1={e.x1} y1={e.y1}
            x2={e.x2} y2={e.y2}
            stroke="#1f2937"
            strokeWidth={1}
            strokeDasharray="4 6"
          />
        ))}

        {/* Pulsos en edges */}
        {edges.map(e => (
          <PulseDot
            key={`pulse-${e.id}`}
            edge={e}
            color="#60a5fa"
          />
        ))}

        {/* Nodos */}
        {nodes.map(node => {
          const color = STATUS_COLORS[node.status] ?? '#4b5563';
          const isHub = node.id === 'hub';
          return (
            <g key={node.id}>
              {/* Glow ring */}
              <circle
                cx={node.cx} cy={node.cy}
                r={node.radius + 6}
                fill="none"
                stroke={color}
                strokeWidth={1}
                opacity={0.3}
              />
              {/* Main circle */}
              <circle
                cx={node.cx} cy={node.cy}
                r={node.radius}
                fill={isHub ? '#1e40af' : '#111827'}
                stroke={color}
                strokeWidth={isHub ? 2 : 1.5}
              />
              {/* Label */}
              <text
                x={node.cx}
                y={node.cy + (isHub ? 6 : node.radius + 20)}
                textAnchor="middle"
                fill={isHub ? '#93c5fd' : '#9ca3af'}
                fontSize={isHub ? 13 : 11}
                fontFamily="monospace"
              >
                {isHub ? 'SGSI' : node.label}
              </text>
              {/* Completion rate inside node (not hub) */}
              {!isHub && (
                <text
                  x={node.cx}
                  y={node.cy + 5}
                  textAnchor="middle"
                  fill={color}
                  fontSize={13}
                  fontWeight="bold"
                  fontFamily="monospace"
                >
                  {Math.round(node.completionRate)}%
                </text>
              )}
            </g>
          );
        })}
      </svg>

      {/* Floating events layer */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {events.map(ev => (
          <FloatingEvent key={ev.id} event={ev} />
        ))}
      </div>

      {/* Legend */}
      <div className="absolute bottom-4 left-6 flex gap-4">
        {Object.entries(STATUS_COLORS).map(([status, color]) => (
          <div key={status} className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
            <span className="text-[10px] text-gray-500 capitalize">
              {status === 'not_started' ? 'Sin inicio'
               : status === 'in_progress' ? 'En progreso'
               : status === 'completed' ? 'Completado'
               : 'No conforme'}
            </span>
          </div>
        ))}
      </div>

      {/* Title */}
      <div className="absolute top-4 left-6">
        <p className="text-xs uppercase tracking-widest text-gray-600">
          Red de Controles — Vista Lógica
        </p>
      </div>
    </div>
  );
});
